import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postAuth, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import type { NedarimTransactionDtoIn } from '@/models/in/NedarimTransactionDtoIn';
import type { FundLookupDtoIn } from '@/models/in/NedarimDecodeModels';
import type { NedarimTransactionFundAllocationDtoOut } from '@/models/out/NedarimTransactionFundAllocationDtoOut';
import Alert, { type AlertType } from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import FundPicker from './FundPicker';
import TablePager, { PAGE_SIZE } from './TablePager';
import { ALLOCATE_FUNDS_URL, CLASSIFY_EXISTING_URL, TRANSACTIONS_WITHOUT_FUND_URL, formatTransactionTime } from './nedarimTransactionsFundAllocation';
import styles from './fundAllocationTabs.module.scss';

const ManualAssignmentTab = ({ funds }: { funds: FundLookupDtoIn[] }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: transactions, isLoading, isError, refetch } = useGetAuthApi<NedarimTransactionDtoIn[]>(TRANSACTIONS_WITHOUT_FUND_URL);
  const [message, setMessage] = useState<{ type: AlertType; text: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [page, setPage] = useState(0);

  const assign = async (transaction: NedarimTransactionDtoIn, fund: FundLookupDtoIn) => {
    setBusyId(transaction.id);
    setMessage(null);
    try {
      const body: NedarimTransactionFundAllocationDtoOut[] = [{ transactionId: String(transaction.id), fundId: fund.id }];
      await putAuth<unknown, object, NedarimTransactionFundAllocationDtoOut[]>(ALLOCATE_FUNDS_URL, headers, {}, body);
      setMessage({ type: 'success', text: t('nedarimTransactionsFundAllocation.tabs.assigned', { donor: transaction.clientName, fundId: fund.id, fundName: fund.name }) });
      await refetch();
    } catch {
      setMessage({ type: 'error', text: t('nedarimTransactionsFundAllocation.tabs.assignFailed') });
    } finally {
      setBusyId(null);
    }
  };

  const runBackfill = async () => {
    setIsBackfilling(true);
    setMessage(null);
    try {
      const classified = await postAuth<number>(CLASSIFY_EXISTING_URL, headers);
      setMessage({ type: 'success', text: t('nedarimTransactionsFundAllocation.tabs.backfillDone', { count: classified }) });
      await refetch();
    } catch {
      setMessage({ type: 'error', text: t('nedarimTransactionsFundAllocation.tabs.backfillFailed') });
    } finally {
      setIsBackfilling(false);
    }
  };

  if (isLoading) return <div className={styles.loading}>{t('loading')}</div>;

  // A failed request must not read as "nothing here yet": an empty rules table looked exactly like a
  // working screen with no rules while the server was in fact answering with an error.
  if (isError) return <div className={styles.loadError}>{t('nedarimTransactionsFundAllocation.tabs.loadError')}</div>;

  const rows = transactions ?? [];

  // An assignment removes its row, so the current page may fall off the end — clamp, don't reset.
  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(pageCount - 1, 0));
  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className={styles.tabPanel}>
      <p className={`body-md-regular ${styles.intro}`}>{t('nedarimTransactionsFundAllocation.tabs.manualIntro')}</p>

      <div className={styles.toolbar}>
        <span className={`body-sm-regular ${styles.count}`}>{t('nedarimTransactionsFundAllocation.tabs.pendingCount', { count: rows.length })}</span>
        <Button variant="outline" onClick={runBackfill} disabled={isBackfilling}>
          {isBackfilling ? t('nedarimTransactionsFundAllocation.tabs.backfilling') : t('nedarimTransactionsFundAllocation.tabs.backfill')}
        </Button>
      </div>

      {message && <Alert type={message.type} message={message.text} />}

      {rows.length === 0 ? (
        <div className={styles.tableCard}>
          <div className={styles.emptyState}>
            <div className={styles.big}>✓</div>
            <p className="body-md-semibold">{t('nedarimTransactionsFundAllocation.tabs.allAssigned')}</p>
            <p className="body-sm-regular">{t('nedarimTransactionsFundAllocation.tabs.allAssignedHint')}</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.scroll}>
            <table>
              <thead>
                <tr>
                  <th>{t('nedarimTransactionsFundAllocation.columns.clientName')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.groupe')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.comments')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.amount')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.transactionTime')}</th>
                  <th className={styles.assignCol}>{t('nedarimTransactionsFundAllocation.tabs.assignToFund')}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((tx) => (
                  <tr key={tx.id}>
                    <td className={`body-sm-regular ${styles.donor}`}>{tx.clientName || '—'}</td>
                    <td className="body-sm-regular">
                      <span className={styles.src}>{tx.groupe || <span className={styles.muted}>—</span>}</span>
                    </td>
                    <td className="body-sm-regular">
                      <span className={styles.src}>
                        {tx.comments || <span className={styles.muted}>—</span>}
                        {/* A row here with a fund on it is one the sync placed under a fund that doesn't
                            exist. Saying so is the difference between "we couldn't read this" and "the
                            donor asked for a fund number that isn't ours". Zero is the sync's own way of
                            writing "no fund", not a number anybody read off the donation. */}
                        {tx.fundId != null && tx.fundId !== 0 && (
                          <span className={styles.unknownFundPill}>{t('nedarimTransactionsFundAllocation.tabs.unknownFundBadge', { fundId: tx.fundId })}</span>
                        )}
                      </span>
                    </td>
                    <td className={`body-sm-regular ${styles.amount}`}>{tx.amount}</td>
                    <td className={`body-sm-regular ${styles.date}`}>{formatTransactionTime(tx.transactionTime)}</td>
                    <td className={styles.assignCol}>
                      <FundPicker funds={funds} disabled={busyId === tx.id} onConfirm={(fund) => assign(tx, fund)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TablePager page={safePage} pageCount={pageCount} onChange={setPage} />
    </div>
  );
};

export default ManualAssignmentTab;
